/**
 * help/help-index.js
 *
 * Índice central de ayuda del sistema. Espeja la estructura del menú principal
 * y del menú de configuración (ruedita).
 *
 * Contenido detallado por pantalla en help/content/*.js
 */

import { ADM_SAL_HELP } from './content/adm-sal.js'
import { ADM_PUR_HELP } from './content/adm-pur.js'
import { ADM_COL_HELP } from './content/adm-col.js'
import { ADM_PAY_HELP } from './content/adm-pay.js'
import { ADM_EXP_HELP } from './content/adm-exp.js'
import { ADM_PRO_HELP } from './content/adm-pro.js'
import { ADM_COM_HELP } from './content/adm-com.js'
import { ADM_SALES_TERMINAL_HELP } from './content/adm-sales-terminal.js'
import { FIN_CAS_HELP } from './content/fin-cas.js'
import { FIN_BAN_HELP } from './content/fin-ban.js'
import { FIN_CRC_HELP } from './content/fin-crc.js'
import { FIN_OCC_HELP } from './content/fin-occ.js'
import { FIN_CHR_HELP } from './content/fin-chr.js'
import { FIN_CHE_HELP } from './content/fin-che.js'
import { FIN_QRC_HELP } from './content/fin-qrc.js'
import { FIN_CRR_HELP } from './content/fin-crr.js'
import { LAB_EMP_HELP } from './content/lab-emp.js'
import { LAB_SAL_HELP } from './content/lab-sal.js'
import { LAB_CAT_HELP } from './content/lab-cat.js'
import { LAB_CON_HELP } from './content/lab-con.js'
import { LAB_REC_HELP } from './content/lab-rec.js'
import { LAB_LSD_HELP } from './content/lab-lsd.js'
import { TAX_SET_HELP } from './content/tax-set.js'
import { TAX_PER_HELP } from './content/tax-per.js'
import { TAX_SUF_HELP } from './content/tax-suf.js'
import { TAX_IVA_HELP } from './content/tax-iva.js'
import { TAX_OBL_HELP } from './content/tax-obl.js'
import { ACC_CFG_HELP } from './content/acc-cfg.js'
import { ACC_JOU_HELP } from './content/acc-jou.js'
import { ACC_LIB_HELP } from './content/acc-lib.js'
import { MET_SAL_HELP } from './content/met-sal.js'
import { MET_ART_HELP } from './content/met-art.js'
import { MET_TER_HELP } from './content/met-ter.js'
import { MET_PUR_HELP } from './content/met-pur.js'
import { MET_FIN_HELP } from './content/met-fin.js'
import { MET_SUB_HELP } from './content/met-sub.js'
import { MET_PRO_HELP, MET_BOA_HELP } from './content/met-pro-boa.js'
import { MET_HOT_HELP } from './content/met-hot.js'
import { COS_HELP } from './content/cos.js'
import { HOT_HELP } from './content/hot.js'
import { MEL_CON_HELP } from './content/mel-con.js'
import { MEL_PUB_HELP } from './content/mel-pub.js'
import { MEL_ORD_HELP } from './content/mel-ord.js'
import { MEL_QUE_HELP } from './content/mel-que.js'
import { SETTINGS_COMPANY_HELP } from './content/settings-company.js'
import { SETTINGS_USERS_HELP } from './content/settings-users.js'
import { SETTINGS_CURRENCIES_HELP } from './content/settings-currencies.js'
import { SETTINGS_VOUCHERS_HELP } from './content/settings-vouchers.js'
import { SETTINGS_TERMINAL_HELP } from './content/settings-terminal.js'
import { SETTINGS_DEFAULTS_HELP } from './content/settings-defaults.js'
import { SETTINGS_NOTIFICATIONS_HELP } from './content/settings-notifications.js'
import { SETTINGS_PAY_CARDS_HELP } from './content/settings-pay-cards.js'
import { SETTINGS_PASSKEYS_HELP } from './content/settings-passkeys.js'
import { SETTINGS_AI_ASSISTANT_HELP } from './content/settings-ai-assistant.js'
import { AGR_CAM_HELP } from './content/agr-cam.js'
import { BOD_VAS_HELP, BOD_COS_HELP, BOD_ELA_HELP, BOD_FRA_HELP, BOD_MAE_HELP, BOD_ORD_HELP } from './content/bod.js'
import { AGR_CUL_HELP } from './content/agr-cul.js'
import { AGR_EVE_HELP } from './content/agr-eve.js'
import { SUP_PWD_HELP } from './content/sup-pwd.js'

/** @typedef {{ label: string, description: string }} HelpFieldDoc */
/** @typedef {{ title: string, intro?: string, bullets?: string[], paragraphs?: string[], fields?: HelpFieldDoc[] }} HelpDetailSection */
/** @typedef {{ sections: HelpDetailSection[] }} HelpSidebarDetail */
/** @typedef {{ id: string, label: string, description: string, screenId?: string, detail?: HelpSidebarDetail }} HelpSidebarItem */
/** @typedef {{ id: string, label: string, placeholder?: boolean }} HelpOutlineItem */
/** @typedef {{ title: string, items: HelpOutlineItem[] }} HelpOutlineSection */

/**
 * @typedef {Object} HelpScreen
 * @property {string} screenId
 * @property {string} label
 * @property {string} navPath
 * @property {string[]} keywords
 * @property {'pending'|'draft'|'complete'} status
 * @property {string} [summary]
 * @property {HelpSidebarItem[]} [sidebarItems]
 * @property {HelpOutlineSection[]} [outline]
 * @property {HelpScreen[]} [subScreens]
 */

/**
 * @typedef {Object} HelpSection
 * @property {string} id
 * @property {string} label
 * @property {HelpScreen[]} screens
 */

/** Esqueleto común mientras no hay contenido detallado. */
const DEFAULT_OUTLINE = [
  {
    title: 'Pantalla',
    items: [
      { id: 'overview', label: 'Descripción general y propósito', placeholder: true },
      { id: 'layout', label: 'Estructura y zonas de la pantalla', placeholder: true },
    ],
  },
  {
    title: 'Inputs y filtros',
    items: [
      { id: 'filters', label: 'Filtros, selectores y campos de búsqueda', placeholder: true },
      { id: 'forms', label: 'Formularios de alta y edición', placeholder: true },
    ],
  },
  {
    title: 'Botones y acciones',
    items: [
      { id: 'toolbar', label: 'Acciones principales (alta, guardar, exportar…)', placeholder: true },
      { id: 'row-actions', label: 'Acciones por fila o registro', placeholder: true },
      { id: 'modals', label: 'Modales y ventanas secundarias', placeholder: true },
    ],
  },
]

/** @param {string} label @param {string} navPath @param {string[]} [keywords] @param {string} [screenId] */
const screen = (screenId, label, navPath, keywords = []) => ({
  screenId,
  label,
  navPath,
  keywords: [label, navPath, ...keywords].map((k) => k.toLowerCase()),
  status: 'pending',
  outline: DEFAULT_OUTLINE,
})

/** @type {HelpSection[]} */
export const HELP_SECTIONS = [
  {
    id: 'hot',
    label: 'HOTEL',
    screens: [
      HOT_HELP,
    ],
  },
  {
    id: 'agr',
    label: 'AGRO',
    screens: [
      AGR_CAM_HELP,
      AGR_CUL_HELP,
      AGR_EVE_HELP,
    ],
  },
  {
    id: 'bod',
    label: 'BODEGA',
    screens: [
      BOD_VAS_HELP,
      BOD_COS_HELP,
      BOD_ELA_HELP,
      BOD_FRA_HELP,
      BOD_ORD_HELP,
      BOD_MAE_HELP,
    ],
  },
  {
    id: 'adm',
    label: 'ADMINISTRACION',
    screens: [
      ADM_SAL_HELP,
      ADM_PUR_HELP,
      ADM_COL_HELP,
      ADM_PAY_HELP,
      ADM_EXP_HELP,
      ADM_PRO_HELP,
      ADM_COM_HELP,
      ADM_SALES_TERMINAL_HELP,
    ],
  },
  {
    id: 'fin',
    label: 'FINANZAS',
    screens: [
      FIN_CAS_HELP,
      FIN_BAN_HELP,
      FIN_CRC_HELP,
      FIN_OCC_HELP,
      FIN_CHR_HELP,
      FIN_CHE_HELP,
      FIN_QRC_HELP,
      FIN_CRR_HELP,
    ],
  },
  {
    id: 'lab',
    label: 'LABORAL',
    screens: [
      LAB_EMP_HELP,
      LAB_SAL_HELP,
      LAB_CAT_HELP,
      LAB_CON_HELP,
      LAB_REC_HELP,
      LAB_LSD_HELP,
    ],
  },
  {
    id: 'tax',
    label: 'IMPUESTOS',
    screens: [
      TAX_SET_HELP,
      TAX_PER_HELP,
      TAX_SUF_HELP,
      TAX_IVA_HELP,
      TAX_OBL_HELP,
    ],
  },
  {
    id: 'acc',
    label: 'CONTABILIDAD',
    screens: [
      ACC_CFG_HELP,
      ACC_JOU_HELP,
      ACC_LIB_HELP,
    ],
  },
  {
    id: 'met',
    label: 'METRICAS',
    screens: [
      MET_SAL_HELP,
      MET_ART_HELP,
      MET_TER_HELP,
      MET_PUR_HELP,
      MET_FIN_HELP,
      MET_SUB_HELP,
      MET_PRO_HELP,
      MET_BOA_HELP,
      MET_HOT_HELP,
    ],
  },
  {
    id: 'cos',
    label: 'COSTEO',
    screens: [
      COS_HELP,
    ],
  },
  {
    id: 'sup',
    label: 'SOPORTE',
    screens: [
      SUP_PWD_HELP,
    ],
  },
  {
    id: 'mel',
    label: 'MERCADOLIBRE',
    screens: [
      MEL_CON_HELP,
      MEL_PUB_HELP,
      MEL_ORD_HELP,
      MEL_QUE_HELP,
    ],
  },
  {
    id: 'settings',
    label: 'CONFIGURACION',
    screens: [
      SETTINGS_COMPANY_HELP,
      SETTINGS_USERS_HELP,
      SETTINGS_CURRENCIES_HELP,
      SETTINGS_VOUCHERS_HELP,
      SETTINGS_TERMINAL_HELP,
      SETTINGS_DEFAULTS_HELP,
      SETTINGS_NOTIFICATIONS_HELP,
      SETTINGS_PAY_CARDS_HELP,
      SETTINGS_AI_ASSISTANT_HELP,
      SETTINGS_PASSKEYS_HELP,
      screen('settings-help', 'Ayuda', 'Configuración → Ayuda', ['tutorial', 'manual', 'documentación']),
    ],
  },
]

export default HELP_SECTIONS
